// =============================================================================
// useClinicCommunicateSettings — settings UI for Clinio Communicate (CLIDEV-381)
// =============================================================================
// Wraps communicate-status / communicate-provision / communicate-update-config.
// Never exposes apiKey or webhookSecret (status returns hasWebhookSecret only).
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';

export type CommunicateConnectionStatus = 'pending' | 'active' | 'disabled';

/** Sanitized status from communicate-status — no secrets. */
export interface CommunicateStatus {
  connected: boolean;
  status: CommunicateConnectionStatus | null;
  sorId: string | null;
  ean: string | null;
  medicalSpecialityCode: string | null;
  hasWebhookSecret: boolean;
}

export interface CommunicateProvisionOutput {
  clinicId: string;
  status: string;
  alreadyProvisioned: boolean;
  connectionId?: string;
}

export interface CommunicateUpdateConfigOutput {
  clinicId: string;
  status: string;
  sorId: string | null;
  ean: string | null;
  medicalSpecialityCode: string | null;
}

export const communicateStatusQueryKey = (clinicId: string | null | undefined) =>
  ['clinicCommunicate', 'status', clinicId] as const;

export function useCommunicateStatus(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: communicateStatusQueryKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<CommunicateStatus> => {
      if (!clinicId) {
        return {
          connected: false,
          status: null,
          sorId: null,
          ean: null,
          medicalSpecialityCode: null,
          hasWebhookSecret: false,
        };
      }
      return await callFunction<{ clinicId: string }, CommunicateStatus>('communicate-status', {
        clinicId,
      });
    },
  });
}

export function useProvisionCommunicate(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<CommunicateProvisionOutput> => {
      if (!clinicId) throw new Error('clinicId is required');
      return await callFunction<{ clinicId: string }, CommunicateProvisionOutput>(
        'communicate-provision',
        { clinicId },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: communicateStatusQueryKey(clinicId) });
      qc.invalidateQueries({ queryKey: ['care-communications', 'connection', clinicId] });
    },
  });
}

export function useUpdateCommunicateConfig(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      sorId?: string | null;
      ean?: string | null;
      medicalSpecialityCode?: string | null;
    }): Promise<CommunicateUpdateConfigOutput> => {
      if (!clinicId) throw new Error('clinicId is required');
      return await callFunction<
        {
          clinicId: string;
          sorId?: string | null;
          ean?: string | null;
          medicalSpecialityCode?: string | null;
        },
        CommunicateUpdateConfigOutput
      >('communicate-update-config', {
        clinicId,
        ...input,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: communicateStatusQueryKey(clinicId) });
      qc.invalidateQueries({ queryKey: ['care-communications', 'connection', clinicId] });
    },
  });
}
